import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  CircleHelp,
  Clock3,
  FileSpreadsheet,
  FileText,
  Laptop,
  Mail,
  Share2,
  Smartphone,
  Tablet,
  TrendingDown,
  UsersRound,
} from "lucide-react";
import { toast } from "react-toastify";
import { SurveyService } from "../../service/saveSurvey";
import { getMySurveyOverview } from "../../service/mySurvey";
import { AudienceAnalytics, QuestionAnalytics, AiInsights } from "./SurveyAnalyticsTabs";
import SurveyResponses from "./SurveyResponses";
import { getResultData, mergeSurveyOverview, getDeviceBreakdown } from "../../utils/surveyResults";

const tabs = ["Overview", "Responses", "Question Analytics", "Audience Analytics", "AI Insights"];

const getApiMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const formatDate = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const asNumber = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const calculateCompletionRate = (survey) => {
  if (survey?.completion_rate !== undefined && survey?.completion_rate !== null) {
    return String(survey.completion_rate).includes("%")
      ? String(survey.completion_rate)
      : `${survey.completion_rate}%`;
  }

  const responses = asNumber(survey?.responses);
  const target = asNumber(survey?.target_responses);
  if (responses === null || !target) return "—";
  return `${Math.round((responses / target) * 100)}%`;
};

const statusLabel = (status) => {
  const labels = { 0: "Draft", 1: "Active", 2: "Closed", 3: "Deleted" };
  return labels[status] || "Unknown";
};

const SurveyResults = () => {
  const { surveyId } = useParams();
  const location = useLocation();
  const routeSurvey = location.state?.survey;
  const [activeTab, setActiveTab] = useState(
    location.state?.tab === "responses" ? "Responses" : "Overview"
  );
  const [survey, setSurvey] = useState(routeSurvey || null);
  const [responseRows, setResponseRows] = useState([]);
  const [resultDetails, setResultDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const popoverRef = useRef(null);
  const loadId = useRef(0);

  const loadSurvey = useCallback(async () => {
    if (!surveyId) return;

    const id = ++loadId.current;
    setLoading(true);
    setError("");
    const requests = [
      ["Survey details", () => SurveyService.getSurveyDetails(surveyId)],
      ["Survey questions", () => SurveyService.getSurveyQuestions(surveyId)],
      ["Survey overview", () => getMySurveyOverview(surveyId)],
    ];
    const results = await Promise.allSettled(requests.map(async ([, request]) => getResultData(await request())));
    if (id !== loadId.current) return;
    const [details, questions, overview] = results.map((result) => result.status === "fulfilled" ? result.value : null);
    const messages = results.flatMap((result, index) => result.status === "rejected" ? [`${requests[index][0]}: ${getApiMessage(result.reason, "Unable to load data.")}`] : []);
    setSurvey(details || overview?.survey ? mergeSurveyOverview({ ...(routeSurvey || {}), ...(details || {}) }, overview || {}) : routeSurvey || null);
    setResultDetails({ ...(overview || {}), questions: Array.isArray(questions) ? questions : [], overview_available: overview != null });
    setError(messages.join(" "));
    setLoading(false);
  }, [routeSurvey, surveyId]);

  useEffect(() => {
    setSurvey(routeSurvey || null);
    setResultDetails({});
    setResponseRows([]);
    loadSurvey();
    return () => { loadId.current += 1; };
  }, [loadSurvey, routeSurvey]);

  useEffect(() => {
    const close = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setExportOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Dashboard link copied to clipboard.");
      setExportOpen(false);
    } catch {
      toast.error("Unable to copy the dashboard link.");
    }
  };

  if (loading && !survey) {
    return (
      <div className="survey-results-page">
        <Link to="/my-surveys" className="survey-results-back"><ArrowLeft size={15} /> Back</Link>
        <div className="survey-empty-tab">
          <div className="spinner-border spinner-border-sm text-danger" role="status" />
          <h2>Loading survey results</h2>
          <p>Fetching the latest survey data.</p>
        </div>
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="survey-results-page">
        <Link to="/my-surveys" className="survey-results-back"><ArrowLeft size={15} /> Back</Link>
        <div className="survey-empty-tab">
          <div><CircleHelp size={28} /></div>
          <h2>Survey results unavailable</h2>
          <p>{error}</p>
          <button type="button" className="survey-export-button" onClick={loadSurvey}>Try again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="survey-results-page" ref={popoverRef}>
      <Link to="/my-surveys" className="survey-results-back"><ArrowLeft size={15} /> Back</Link>

      {error && (
        <div className="alert alert-warning py-2 px-3 ft-12" role="alert">
          {error}
        </div>
      )}

      <section className="survey-results-header">
        <div>
          <h1>{survey.survey_title || "Untitled Survey"}</h1>
          <p>{Number(survey.status) === 2 ? "Closed On" : "Last Updated"}: {formatDate(survey.updated_on)}</p>
        </div>
        <div className="survey-export-wrap">
          <button type="button" className="survey-export-button" onClick={() => setExportOpen((value) => !value)}>
            Export &amp; Share <ChevronDown size={16} className={exportOpen ? "rotate-180" : ""} />
          </button>
          {exportOpen && (
            <div className="survey-export-menu">
              <span className="survey-menu-heading">Export data</span>
              <button type="button" onClick={() => { setExportOpen(false); window.print(); }}>
                <FileText className="pdf-icon" /><span><strong>PDF report</strong><small>Print or save this dashboard as PDF</small></span>
              </button>
              <button type="button" disabled={!responseRows.length} onClick={() => downloadCsv(responseRows, surveyId)}>
                <FileSpreadsheet className="sheet-icon" /><span><strong>Google Sheet</strong><small>Download the current response page to import</small></span>
              </button>
              <button type="button" disabled={!responseRows.length} onClick={() => downloadCsv(responseRows, surveyId)}>
                <FileSpreadsheet className="csv-icon" /><span><strong>CSV</strong><small>Current response page · open Responses to export</small></span>
              </button>
              <div className="survey-menu-divider" />
              <span className="survey-menu-heading">Share</span>
              <button type="button" onClick={handleCopyLink}>
                <Share2 /><span><strong>Copy Share Link</strong><small>Dashboard link</small></span>
              </button>
              <button type="button" onClick={() => { window.location.href = `mailto:?subject=${encodeURIComponent(survey.survey_title || "Survey dashboard")}&body=${encodeURIComponent(window.location.href)}`; setExportOpen(false); }}>
                <Mail /><span><strong>Email This Dashboard</strong></span>
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="survey-results-content">
        <nav className="survey-results-tabs" aria-label="Survey results sections">
          {tabs.map((tab) => (
            <button key={tab} type="button" className={activeTab === tab ? "active" : ""} onClick={() => setActiveTab(tab)}>{tab}</button>
          ))}
        </nav>

        {activeTab === "Overview" && <Overview survey={survey} details={resultDetails} />}
        {activeTab === "Responses" && (
          <SurveyResponses surveyId={surveyId} onRowsChange={setResponseRows} onDeleted={loadSurvey} />
        )}
        {activeTab === "Question Analytics" && (
          <QuestionAnalytics details={resultDetails} rows={[]} />
        )}
        {activeTab === "Audience Analytics" && (
          <AudienceAnalytics details={resultDetails} rows={[]} total={asNumber(survey.responses) ?? 0} />
        )}
        {activeTab === "AI Insights" && (
          <AiInsights details={resultDetails} total={asNumber(survey.responses) ?? 0} />
        )}
      </section>
      <span className="visually-hidden">Survey ID: {surveyId}</span>
    </div>
  );
};

const downloadCsv = (rows, surveyId) => {
  const quote = (value) => {
    const text = String(value ?? "");
    const safe = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
    return `"${safe.replace(/"/g, '""')}"`;
  };
  const data = [["Respondent", "Submitted", "Time Taken", "Device", "Status"], ...rows.map((row) => [row.respondent_name || row.user_name || row.name, row.submitted_at || row.created_at, row.time_taken || row.duration, row.device_type || row.device, row.status || row.response_status])];
  const url = URL.createObjectURL(new Blob(["\uFEFF", data.map((row) => row.map(quote).join(",")).join("\r\n")], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `survey-${surveyId}-responses.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

const Overview = ({ survey, details }) => {
  const responses = asNumber(survey.responses);
  const target = asNumber(survey.target_responses);
  const percentage = asNumber(details.survey_status?.response_percentage) ?? (responses !== null && target ? Math.round((responses / target) * 100) : null);
  const trend = Array.isArray(details.response_trend) ? details.response_trend : [];
  const { devices: deviceCounts, total: totalSessions } = getDeviceBreakdown(details);

  return (
    <div className="survey-overview">
      <h2>Survey Dashboard</h2>
      <div className="metric-grid">
        <Metric title="Total Responses" value={responses?.toLocaleString() ?? "—"} icon={<UsersRound />} />
        <Metric title="Completion Rate" value={calculateCompletionRate(survey)} icon={<Check />} />
        <Metric title="Avg. Completion Time" value={survey.avg_completion_time || details.avg_completion_time || "—"} icon={<Clock3 />} />
        <Metric title="Drop-off Rate" value={survey.drop_off_rate != null ? `${String(survey.drop_off_rate).replace(/%$/, "")}%` : "—"} icon={<TrendingDown />} />
      </div>

      <div className="overview-chart-row">
        <div className="response-trend-card">
          <div className="chart-card-title"><h3>Response Trend</h3><span className="survey-chart-period">Daily</span></div>
          <TrendChart points={trend} />
        </div>
        <div className="survey-status-card">
          <div className="status-card-title"><h3>Survey Status</h3><span>{details.survey_status?.status_label || statusLabel(Number(survey.status))}</span></div>
          <div className="status-dates">
            <p>Start Date<strong>{formatDate(survey.start_date)}</strong></p>
            <p>End Date<strong>{formatDate(survey.end_date)}</strong></p>
          </div>
          <p className="status-stat">Target Responses<strong>{target ?? "—"}</strong></p>
          <p className="status-stat">Responses Received<strong>{responses ?? "—"}{percentage !== null ? ` (${percentage}%)` : ""}</strong></p>
        </div>
      </div>
      <div className="key-metrics-card"><h3>Key Metrics</h3><div className="device-breakdown-title"><strong>Device Breakdown</strong><span>Total Sessions<strong>{totalSessions?.toLocaleString() ?? "—"}</strong></span></div><div className="device-metrics">{deviceCounts.map(({ name, count, percent }) => { const Icon = name === "Desktop" ? Laptop : name === "Mobile" ? Smartphone : Tablet; return <div className="device-metric" key={name}><div className="device-label"><i><Icon size={16} /></i>{name === "Desktop" ? "Desktop/Laptop" : name}</div><strong>{percent != null ? `${percent}%` : "—"}</strong><small>{count?.toLocaleString() ?? "—"} sessions</small><span className="device-track"><i style={{ width: `${percent ?? 0}%`, background: "linear-gradient(90deg,#d32235,#ffad68)" }} /></span></div>; })}</div></div>
    </div>
  );
};

const TrendChart = ({ points }) => {
  if (!points.length) return <div className="trend-empty">Response history is not available yet.</div>;
  const values = points.map((point) => asNumber(point.count ?? point.responses ?? point.value) ?? 0);
  const max = Math.max(1, ...values);
  const coordinates = values.map((value, index) => `${20 + index * 460 / Math.max(1, values.length - 1)},${165 - value / max * 140}`);
  return <div className="trend-chart"><svg viewBox="0 0 500 190" role="img" aria-label="Response trend chart" preserveAspectRatio="none"><defs><linearGradient id="responseArea" x1="0" y1="0" x2="0" y2="1"><stop stopColor="#fbdcde" /><stop offset="1" stopColor="#fff" /></linearGradient></defs>{[25, 72, 119, 165].map((y) => <line key={y} x1="20" x2="480" y1={y} y2={y} stroke="#e9eaec" strokeDasharray="2 3" />)}<polygon points={`20,165 ${coordinates.join(" ")} 480,165`} fill="url(#responseArea)" /><polyline points={coordinates.join(" ")} fill="none" stroke="#e22d3d" strokeWidth="2" />{coordinates.map((point, index) => { const [cx, cy] = point.split(","); return <circle key={index} cx={cx} cy={cy} r="3" fill="#fff" stroke="#e22d3d" />; })}</svg><div className="trend-labels">{points.map((point, index) => <span key={index}>{point.label || point.date || ""}</span>)}</div></div>;
};

const Metric = ({ title, value, icon }) => (
  <div className="metric-card">
    <div className="metric-title"><span>{title}</span><i>{icon}</i></div>
    <strong>{value}</strong>
    <div className="metric-change"><span>Updated from server</span><em>Live</em></div>
  </div>
);

export default SurveyResults;
